/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer, useState } from "react";
import PropTypes from "prop-types";
import { formatCurrency } from "../../utils/helpers";

let porcentajeImpuesto = 0;
let currentCost = 0;

const redondear = (valor) => Math.round((parseFloat(valor) || 0) * 100) / 100;

/** Precio del empaque calculado desde el precio unidad (los dos con impuesto). */
const paqueteCalculado = (total, cantidadEmpaque) =>
	redondear((parseFloat(total) || 0) * (parseFloat(cantidadEmpaque) || 0));

function reducer(state, action) {
	switch (action.type) {
		case "input":
			return {
				...state,
				[action.payload.field]:
					action.payload.field !== "impuesto"
						? parseFloat(action.payload.value)
						: action.payload.value,
			};
		case "calcTotal":
			const total =
				parseFloat(state.importe) +
				parseFloat(state.importe) * parseFloat(action.payload.value);

			return {
				...state,
				precio_total: total.toFixed(2),
			};
		case "calcCosto":
			// No se puede calcular el % de beneficio sobre un costo 0
			// (división por cero → infinito). Sin costo se deja el % tal cual.
			if (!action.payload.costo) return state;
			const costoItem =
				(state.importe - action.payload.costo) / action.payload.costo;
			return {
				...state,
				costo: (costoItem * 100).toFixed(2),
			};

		case "calcPrecioVentaOnCostoInput":
			// Sin costo no hay base para calcular el precio desde el %.
			if (!action.payload.costo) return state;
			const porcentajeCosto =
				(action.payload.porcentajeCosto / 100) * action.payload.costo;

			return {
				...state,
				importe: parseFloat(
					action.payload.costo + porcentajeCosto
				).toFixed(2),
			};

		case "setImporte":
			return {
				...state,
				importe: action.payload.costo,
			};

		case "setPaquete":
			return {
				...state,
				precio_paquete: action.payload.value,
				paqueteManual: action.payload.manual,
			};

		default:
			break;
	}
}

const defaultPrice = {
	id: Date.now(),
	importe: 0,
	impuesto: 0,
	precio_total: 0,
	costo: 0,
};

// Un precio de paquete guardado (no null) es uno que el usuario fijó a mano;
// null en el backend significa "calculado".
const estadoInicial = (precio) => ({
	...precio,
	precio_paquete: precio.precio_paquete ?? "",
	paqueteManual: precio.precio_paquete != null && precio.precio_paquete !== "",
});

/**
 * Una lista de precio: % beneficio sobre el costo, impuesto, precio unidad y,
 * si el producto tiene empaque, precio paquete. El importe (precio sin
 * impuesto) ya no se captura: viaja oculto porque el backend lo guarda y
 * ventas deriva de él el costo de la línea.
 */
const Precios = ({
	precio = defaultPrice,
	index,
	removePrecio,
	costo,
	rowClasses,
	impuestos = [],
	accion,
	firstTaxSelected,
	disabled,
	errors,
	onTotalChange,
	cantidadEmpaque,
	etiquetaUnidad = "Und",
	etiquetaEmpaque = "Paq",
}) => {
	const [state, dispatch] = useReducer(reducer, precio, estadoInicial);
	const [lastCostoModified, setLastCostoModified] = useState("costo");

	// ¿Hay costo base escrito? Sin él no se puede calcular el % de beneficio
	// (sería infinito), por eso ese campo se mantiene deshabilitado.
	const costoHay = parseFloat(costo?.current?.value) || 0;

	const tieneEmpaque = parseFloat(cantidadEmpaque) > 1;

	useEffect(() => {
		onTotalChange?.(index, parseFloat(state.precio_total) || 0);
	}, [state.precio_total]);

	// Mientras no se toque, el precio paquete sigue al precio unidad y a la
	// cantidad en paquete.
	useEffect(() => {
		if (state.paqueteManual) return;

		dispatch({
			type: "setPaquete",
			payload: {
				value: tieneEmpaque ? paqueteCalculado(state.precio_total, cantidadEmpaque) : "",
				manual: false,
			},
		});
	}, [state.precio_total, cantidadEmpaque, state.paqueteManual]);

	const handleChangeImporte = (value) => {
		setLastCostoModified("importe");
		dispatch({ type: "input", payload: { field: "importe", value } });
		dispatch({ type: "calcTotal", payload: { value: porcentajeImpuesto } });
		dispatch({
			type: "calcCosto",
			payload: { costo: parseFloat(costo?.current?.value) || 0 },
		});
	};

	const handleChangeImpuesto = (value) => {
		porcentajeImpuesto = impuestos.find(
			(impuesto) => impuesto.value === parseInt(value)
		)?.porcentaje;
        porcentajeImpuesto = parseFloat(porcentajeImpuesto) || 0;
		dispatch({ type: "input", payload: { field: "impuesto", value } });

		if (lastCostoModified === "importe") {
			dispatch({
				type: "calcTotal",
				payload: { value: porcentajeImpuesto },
			});
		} else {
			dispatch({
				type: "input",
				payload: {
					field: "importe",
					value: (
						parseFloat(state.precio_total) /
						(porcentajeImpuesto + 1)
					).toFixed(2),
				},
			});
			dispatch({
				type: "calcCosto",
				payload: { costo: parseFloat(costo?.current?.value) || 0 },
			});
		}
	};

	const handleChangePrecioTotal = (value) => {
		setLastCostoModified("precio_total");
		dispatch({ type: "input", payload: { field: "precio_total", value } });

		const precioVenta = parseFloat(value) / (porcentajeImpuesto + 1);
		const costoActual = parseFloat(costo?.current?.value) || 0;

		// change importe value
		dispatch({
			type: "input",
			payload: { field: "importe", value: precioVenta.toFixed(2) },
		});
		// change % beneficio value (sin costo base el % es infinito, se omite)
		if (costoActual) {
			const costoInput = ((precioVenta - costoActual) / costoActual) * 100;
			dispatch({
				type: "input",
				payload: { field: "costo", value: costoInput.toFixed(2) },
			});
		}
	};

	// this references to "costo" input
	const handleBeneficioChange = (value) => {
		setLastCostoModified("importe");
		dispatch({ type: "input", payload: { field: "costo", value } });
		dispatch({
			type: "calcPrecioVentaOnCostoInput",
			payload: {
				porcentajeCosto: value,
				costo: parseFloat(costo?.current?.value) || 0,
			},
		});
		dispatch({
			type: "calcTotal",
			payload: { value: porcentajeImpuesto },
		});
	};

	// Vaciar el campo lo devuelve a calculado.
	const handleChangePaquete = (value) => {
		dispatch({ type: "setPaquete", payload: { value, manual: value !== "" } });
	};

	useEffect(() => {
		if (accion === "crear") {
			porcentajeImpuesto = impuestos[0]?.porcentaje || 0;
		}
	}, [impuestos]);

	useEffect(() => {
		if (accion === "crear") {
			handleChangeImpuesto(firstTaxSelected);
			return;
		}
	}, [firstTaxSelected]);

	useEffect(() => {
		currentCost = costo?.current?.value;
		if (accion === "editar") {
			porcentajeImpuesto =
				impuestos.find(
					(impuesto) => impuesto.value === parseInt(precio.impuesto)
				)?.porcentaje || firstTaxSelected.porcentaje || 0;

			dispatch({
				type: "setImporte",
				payload: {
					porcentajeCosto: state.costo,
					costo: precio?.precio_venta,
				},
			});
		}
	}, []);

	useEffect(() => {
		if (accion === "editar") {
			porcentajeImpuesto =
				impuestos.find(
					(impuesto) => impuesto.value === parseInt(precio.impuesto)
				)?.porcentaje || firstTaxSelected.porcentaje || 0;

			if (currentCost !== costo?.current?.value) {
				handleBeneficioChange(state.costo);
			}
		}
	}, [costo?.current?.value, impuestos]);

	// Si coincide con el calculado se manda vacío (null = calculado), para que
	// siga al precio unidad cuando éste cambie, también desde una compra.
	const precioPaqueteAEnviar =
		tieneEmpaque &&
		state.paqueteManual &&
		redondear(state.precio_paquete) !== paqueteCalculado(state.precio_total, cantidadEmpaque)
			? state.precio_paquete
			: "";

	const error = (campo) =>
		errors && errors[`precios.${index}.${campo}`] && (
			<small className="help-blockParams form-Text d-block">
				{errors[`precios.${index}.${campo}`][0]}
			</small>
		);

	// Importe = precio sin impuesto, sólo para mostrar. El del paquete se saca
	// del precio paquete con el impuesto elegido en esta fila.
	const porcentajeFila =
		parseFloat(
			impuestos.find(
				(impuesto) => impuesto.value === parseInt(state.impuesto || firstTaxSelected)
			)?.porcentaje
		) || 0;
	const importeUnidad = redondear(state.importe).toFixed(2);
	const importePaquete = redondear(
		(parseFloat(state.precio_paquete) || 0) / (porcentajeFila + 1)
	).toFixed(2);

	// % beneficio e impuesto van estrechos; en móvil todo queda de a dos por fila
	// y, con empaque, el importe (que muestra dos valores) ocupa la suya.
	const columnaCorta = "col-6 col-md-2";
	const columnaImporte = tieneEmpaque ? "col-12 col-md-2" : "col-6 col-md-3";
	const columnaPrecio = tieneEmpaque ? "col-6 col-md-3" : "col-12 col-md-5";

	return (
		<div className="precio-item">
			<input
				value={state.id}
				name={`precios[${index}][id]`}
				type="hidden"
			/>
			<input
				type="hidden"
				name={`precios[${index}][precio_venta]`}
				value={state.importe ?? ""}
				disabled={disabled}
			/>
			<input
				type="hidden"
				name={`precios[${index}][precio_paquete]`}
				value={precioPaqueteAEnviar}
				disabled={disabled}
			/>
			<div className="precio-item__encabezado">
				<span className="precio-item__titulo">Precio {index + 1}</span>
				<button
					type="button"
					className="btn btn-link p-0 text-danger"
					aria-label={`Borrar precio ${index + 1}`}
					onClick={() => removePrecio(precio.id)}
					disabled={disabled}
				>
					<i className="fas fa-trash-alt"></i>
				</button>
			</div>
			<div className="form-row">
				<div className={`form-group ${columnaCorta}`}>
					<label className="label form-control-label">
						% Beneficio:
					</label>
					<input
						type="number"
						inputMode="decimal"
						className="text-input form-control"
						id={`precios[${index}][costo]`}
						name={`precios[${index}][costo]`}
						value={state.costo}
						disabled={disabled || !costoHay}
						onChange={(e) =>
							handleBeneficioChange(e.target.value)
						}
						step=".01"
					/>
					{error("costo")}
				</div>
				<div className={`form-group ${columnaCorta}`}>
					<label className="label form-control-label">
						Impuesto:
					</label>
					<select
						type="text"
						className="form-control"
						id={`precios[${index}][impuesto_id]`}
						name={`precios[${index}][impuesto_id]`}
						value={state.impuesto || firstTaxSelected.id}
						onChange={(e) =>
							handleChangeImpuesto(e.target.value)
						}
						disabled={disabled}
					>
						{impuestos.map((impuesto) => (
							<option
								value={impuesto.value}
								key={impuesto.value}
							>
								{impuesto.label}
							</option>
						))}
					</select>
				</div>
				<div className={`form-group ${columnaImporte}`}>
					<label className="label form-control-label">
						Importe:
					</label>
					<div className="precio-item__importe">
						<div>
							{tieneEmpaque && (
								<small className="text-muted">{etiquetaUnidad} </small>
							)}
							<strong>{formatCurrency(importeUnidad)}</strong>
						</div>
						{tieneEmpaque && (
							<div>
								<small className="text-muted">{etiquetaEmpaque} </small>
								<strong>{formatCurrency(importePaquete)}</strong>
							</div>
						)}
					</div>
				</div>
				<div className={`form-group ${columnaPrecio}`}>
					<label className="label form-control-label">
						Precio unidad:
					</label>
					<input
						type="number"
						min={0}
						inputMode="decimal"
						className="text-input form-control"
						id={`precios[${index}][total]`}
						name={`precios[${index}][total]`}
						value={state.precio_total}
						disabled={disabled}
						onChange={(e) =>
							handleChangePrecioTotal(e.target.value)
						}
						step=".01"
					/>
					{error("total")}
					{error("precio_venta")}
				</div>
				{tieneEmpaque && (
					<div className={`form-group ${columnaPrecio}`}>
						<label className="label form-control-label">
							Precio paquete{" "}
							<small className="text-muted">
								{state.paqueteManual ? "(fijo)" : "(calculado)"}
							</small>
							:
						</label>
						<input
							type="number"
							min={0}
							inputMode="decimal"
							className="text-input form-control"
							value={state.precio_paquete}
							disabled={disabled}
							onChange={(e) =>
								handleChangePaquete(e.target.value)
							}
							step=".01"
						/>
						{error("precio_paquete")}
					</div>
				)}
			</div>
		</div>
	);
};

Precios.propTypes = {
	precio: PropTypes.object,
	index: PropTypes.number,
	removePrecio: PropTypes.func,
	costo: PropTypes.object,
	rowClasses: PropTypes.string,
	impuestos: PropTypes.array,
	firstTaxSelected: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
	setParentState: PropTypes.func,
	onTotalChange: PropTypes.func,
	cantidadEmpaque: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	etiquetaUnidad: PropTypes.string,
	etiquetaEmpaque: PropTypes.string,
};

export default Precios;
